import {formatDate, formatNumber} from '@angular/common';
import {Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {Subscription} from 'rxjs';
import {map} from 'rxjs/operators';
import * as echarts from 'echarts';
import {AdminDashboardDataService} from '@features/admin-panel/services/admin-dashboard-data.service';
import {AdminDashboardReportRegistry} from '@features/admin-panel/services/admin-dashboard-report-registry.service';
import {countOf} from '@features/admin-panel/helpers/admin-dashboard-text';
import {adbPct} from '@features/admin-panel/helpers/admin-dashboard-format';
import {ADB_CHART_LIGHT, adbAlpha, adbChartTheme, adbRedrawOnThemeChange, adbTooltip} from '@features/admin-panel/helpers/admin-dashboard-chart-theme';
import {AdminDashboardReportBlock, AdminDashboardReportProvider, AdminDashboardReportStat} from '@features/admin-panel/models/admin-dashboard-report.models';
import {AdminDashboardQueryParams, AdminDashboardWidgetState} from '@features/admin-panel/models/admin-dashboard.models';
import {
  AdminDashboardActivationFunnel,
  AdminDashboardActivationFunnelDrill,
  AdminDashboardActivationStageKey
} from '@features/admin-panel/models/admin-dashboard-activation.models';
import {adbDrillRowView} from '@features/admin-panel/helpers/admin-dashboard-drill';
import {AdminDashboardDrillView} from '@features/admin-panel/models/admin-dashboard-drill.models';
import {AdminDashboardDrillService} from '../drill/admin-dashboard-drill.service';

/** The ECharts entry point behind a plain object, so a spec can stub it (the echarts namespace is read-only). */
export const activationFunnelCharts = {
  init: (element: HTMLElement): echarts.ECharts => echarts.init(element)
};

/** One stage of the funnel: its count and its share of the companies that signed up. */
export interface AdminDashboardActivationStage {
  key: AdminDashboardActivationStageKey;
  label: string;
  value: number;
  /** Share of signed up, 0-1. */
  share: number;
}

/** The four stages in funnel order, with the labels the card shows. */
const STAGES: {key: AdminDashboardActivationStageKey; label: string}[] = [
  {key: 'signed-up', label: 'Signed up'},
  {key: 'first-project', label: 'First project'},
  {key: 'first-scenario', label: 'First scenario'},
  {key: 'has-entities', label: 'Has entities'}
];

/**
 * Activation funnel (ITP-E-217-03, Plan ITP-P-50 Q18): of the companies that joined in the period, how many built a
 * first project, a first scenario and have entities (each stage counted on its own), as a share of signed up, with the
 * median days to the first project and to the first scenario. Reloads whenever the dashboard's query params change.
 */
@Component({
  standalone: false,
  selector: 'app-admin-dashboard-activation-funnel',
  templateUrl: './admin-dashboard-activation-funnel.component.html',
  styleUrls: ['./admin-dashboard-activation-funnel.component.scss']
})
export class AdminDashboardActivationFunnelComponent implements OnChanges, OnInit, OnDestroy, AdminDashboardReportProvider {
  /** Redraws with the new theme's colours when the painted theme flips (GEN-E-137-08). */
  private readonly themeRedraw = adbRedrawOnThemeChange(() => this.render());
  @Input() params: AdminDashboardQueryParams;
  /** A company picked with Evidence in a stage drill (ITP-E-217-04): the page opens its why panel. */
  @Output() companySelected = new EventEmitter<string>();
  /** What the card has to show, reported to the dashboard (ITP-E-173-01). */
  @Output() stateChange = new EventEmitter<AdminDashboardWidgetState>();
  widgetState: AdminDashboardWidgetState = 'loading';

  funnel: AdminDashboardActivationFunnel | null = null;
  stages: AdminDashboardActivationStage[] = [];
  loaded = false;
  /** The last load failed: the card says so instead of looking empty. */
  failed = false;
  readonly description = 'Did the companies that joined in this period build anything?';
  readonly failedTitle = 'Couldn\'t load the activation funnel';

  private chartElement: HTMLDivElement;
  private chart: echarts.ECharts;
  private resizeObserver: ResizeObserver;
  private loadSubscription: Subscription;

  constructor(private dataService: AdminDashboardDataService, private reportRegistry: AdminDashboardReportRegistry,
    private host: ElementRef<HTMLElement>, private drillService: AdminDashboardDrillService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['params']) {
      this.load();
    }
  }

  ngOnInit(): void {
    this.reportRegistry.register('platform-usage', this.host.nativeElement, this);
  }

  /** The funnel chart exists only while there is something to draw: created when its container appears, disposed when it goes. */
  @ViewChild('stageChart') set stageChartContainer(container: ElementRef<HTMLDivElement> | undefined) {
    if (container?.nativeElement === this.chartElement) {
      return;
    }
    this.disposeChart();
    if (container) {
      this.chartElement = container.nativeElement;
      this.chart = activationFunnelCharts.init(this.chartElement);
      this.chart.on('click', (event: any) => this.openStage(this.stages[event?.dataIndex]));
      this.resizeObserver = new ResizeObserver(() => this.chart?.resize());
      this.resizeObserver.observe(this.chartElement);
      this.render();
    }
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.disposeChart();
    this.reportRegistry.unregister(this);
  }

  /**
   * "Median 12 days to first project · 30.5 days to first scenario"; a part whose median is unknown is left out;
   * null when both are.
   */
  get medianText(): string | null {
    const days = (value: number): string => `${formatNumber(value, 'en-US', '1.0-1')} ${value === 1 ? 'day' : 'days'}`;
    const parts: string[] = [];
    const project = this.funnel?.medianDaysToFirstProject;
    const scenario = this.funnel?.medianDaysToFirstScenario;
    if (project !== null && project !== undefined) {
      parts.push(`${days(project)} to first project`);
    }
    if (scenario !== null && scenario !== undefined) {
      parts.push(`${days(scenario)} to first scenario`);
    }
    return parts.length ? `Median ${parts.join(' · ')}` : null;
  }

  /** "8 · 40%": a stage's count and its share of signed up (the label inside the funnel). */
  stageValueText(stage: AdminDashboardActivationStage | undefined): string {
    if (!stage) {
      return '';
    }
    return `${formatNumber(stage.value, 'en-US', '1.0-0')} · ${adbPct(stage.share)}`;
  }

  /** "<b>First project</b><br/>8 companies · 40% of signed up<br/><muted>Click to list them</muted>": a funnel stage's tooltip. */
  stageTooltipText(stage: AdminDashboardActivationStage | undefined, hintColour: string = ADB_CHART_LIGHT.ink3): string {
    if (!stage) {
      return '';
    }
    return `<b>${stage.label}</b><br/>${countOf(stage.value, 'company', 'companies')} · ${adbPct(stage.share)} of signed up`
      + `<br/><span style="color:${hintColour}">Click to list them</span>`;
  }

  /** "Who has not built a project yet" shows while some company that signed up has no first project. */
  get showNotBuilt(): boolean {
    return !!this.funnel && this.funnel.signedUp > this.funnel.firstProject;
  }

  /** A funnel stage was clicked (ITP-E-217-04): the drill lists the companies that reached it. A click outside the stages does nothing. */
  openStage(stage: AdminDashboardActivationStage | undefined): void {
    if (!stage) {
      return;
    }
    this.openDrill(`${stage.label}: reached`, stage.key, true);
  }

  /** "Who has not built a project yet" (ITP-E-217-04): the companies of the cohort with no first project. */
  openNotBuilt(): void {
    this.openDrill('Who has not built a project yet', 'first-project', false);
  }

  /** The card for the PDF report, built from what it shows now; never reloads (ITP-E-146-11). */
  reportBlocks(): AdminDashboardReportBlock[] {
    if (!this.loaded) {
      return [];
    }
    if (this.failed) {
      return [{kind: 'empty', title: 'Activation funnel', text: this.failedTitle}];
    }
    const funnel = this.funnel;
    if (!funnel || !(funnel.signedUp > 0)) {
      return [{kind: 'empty', title: 'Activation funnel', subtitle: this.description, text: 'No companies joined in this period'}];
    }
    const median = (value: number | null | undefined): string =>
      value === null || value === undefined ? '–' : formatNumber(value, 'en-US', '1.0-1');
    return [
      {
        kind: 'stats',
        title: 'Activation funnel',
        subtitle: this.description,
        stats: [
          ...this.stages.map((stage, index): AdminDashboardReportStat => index === 0
            ? {label: stage.label, value: formatNumber(stage.value, 'en-US', '1.0-0')}
            : {label: stage.label, value: formatNumber(stage.value, 'en-US', '1.0-0'), detail: `${adbPct(stage.share)} of signed up`}),
          {label: 'Median days to first project', value: median(funnel.medianDaysToFirstProject)},
          {label: 'Median days to first scenario', value: median(funnel.medianDaysToFirstScenario)}
        ]
      },
      {
        kind: 'chart',
        title: 'Activation funnel by stage',
        image: this.chart ? this.chart.getDataURL({type: 'png', pixelRatio: 2, backgroundColor: '#FFFFFF'}) : null,
        widthPx: this.chart?.getWidth() ?? 0,
        heightPx: this.chart?.getHeight() ?? 0
      }
    ];
  }

  /** Opens the shared drill dialog for one stage and outcome; Evidence on a row emits companySelected. */
  private openDrill(title: string, stage: AdminDashboardActivationStageKey, reached: boolean): void {
    if (!this.params) {
      return;
    }
    const params = this.params;
    this.drillService.open({
      title,
      columns: [{header: 'Joined', align: 'end'}, {header: 'Reached', align: 'end'}],
      load: () => this.dataService.getWithExtra<AdminDashboardActivationFunnelDrill>(
        'AdminDashboardActivationFunnelDrillQuery', params, {Stage: stage, Reached: reached}
      ).pipe(map(drill => this.drillView(drill)))
    }).subscribe(result => {
      if (result?.action === 'evidence') {
        this.companySelected.emit(result.companyId);
      }
    });
  }

  /** "2 companies that joined 15 Sep – 15 Oct 2026.", then Joined and Reached (d MMM yyyy, "–" when not known) per company. */
  private drillView(drill: AdminDashboardActivationFunnelDrill | null | undefined): AdminDashboardDrillView {
    const day = (iso: string): string => formatDate(iso, 'd MMM yyyy', 'en-US', 'UTC');
    const rows = (drill?.Rows ?? []).map(row => adbDrillRowView(row, [
      {text: day(row.joinedOn)},
      {text: row.reachedOn ? day(row.reachedOn) : '–'}
    ]));
    const total = drill?.Total ?? rows.length;
    return {
      subtitle: drill?.From && drill?.To
        ? `${countOf(total, 'company', 'companies')} that joined ${formatDate(drill.From, 'd MMM', 'en-US', 'UTC')} – ${day(drill.To)}.`
        : '',
      rows,
      total
    };
  }

  private load(): void {
    this.loadSubscription?.unsubscribe();
    if (!this.params) {
      return;
    }
    // A reload keeps showing the last result until the new one arrives, so only the first load reports 'loading'.
    if (!this.loaded) {
      this.report('loading');
    }
    this.loadSubscription = this.dataService.getData<AdminDashboardActivationFunnel>(
      'AdminDashboardActivationFunnelQuery', this.params, 'Funnel'
    ).subscribe({
      next: funnel => this.show(funnel ?? null, false),
      error: () => this.show(null, true)
    });
  }

  private show(funnel: AdminDashboardActivationFunnel | null, failed: boolean): void {
    this.funnel = funnel;
    const signedUp = funnel?.signedUp ?? 0;
    const values: {[key: string]: number} = {
      'signed-up': signedUp,
      'first-project': funnel?.firstProject ?? 0,
      'first-scenario': funnel?.firstScenario ?? 0,
      'has-entities': funnel?.hasEntities ?? 0
    };
    this.stages = funnel
      ? STAGES.map(stage => ({...stage, value: values[stage.key], share: signedUp > 0 ? values[stage.key] / signedUp : 0}))
      : [];
    this.failed = failed;
    this.loaded = true;
    this.render();
    this.report(failed ? 'failed' : signedUp > 0 ? 'content' : 'empty');
  }

  private report(state: AdminDashboardWidgetState): void {
    this.widgetState = state;
    this.stateChange.emit(state);
  }

  private disposeChart(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.chart?.dispose();
    this.chart = null;
    this.chartElement = null;
  }

  /**
   * The funnel (the trial funnel's recipe, ITP-E-153-02): Signed up in the light blue, First project in violet (the
   * theme purple at 55%), First scenario in the theme orange at 70%, Has entities in the bar green; labels inside.
   * `max` is the signed-up count, so Signed up fills the width.
   */
  private render(): void {
    if (!this.chart) {
      return;
    }
    const t = adbChartTheme(this.host.nativeElement);
    const colours: {[key: string]: string} = {
      'signed-up': t.blue2,
      'first-project': adbAlpha(t.purple, 0.55),
      'first-scenario': adbAlpha(t.orange, 0.7),
      'has-entities': t.greenBar
    };
    const option: echarts.EChartsOption = {
      tooltip: adbTooltip(t, {
        trigger: 'item',
        formatter: (point: any) => this.stageTooltipText(this.stages[point?.dataIndex], t.ink3)
      }),
      series: [{
        type: 'funnel',
        left: 8,
        right: 8,
        top: 8,
        bottom: 8,
        min: 0,
        max: Math.max(this.funnel?.signedUp ?? 0, 1),
        sort: 'none',
        gap: 3,
        minSize: '30%',
        label: {
          show: true,
          position: 'inside',
          color: t.ink,
          fontSize: 12,
          formatter: (point: any) => `${point.name}  ${this.stageValueText(this.stages[point?.dataIndex])}`
        },
        itemStyle: {borderWidth: 0, borderRadius: 4},
        data: this.stages.map(stage => ({name: stage.label, value: stage.value, itemStyle: {color: colours[stage.key]}}))
      }]
    };
    this.chart.setOption(option, true);
  }
}
